import React, { memo, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { Icon, Spinner, Typography } from '../index'
import { LanguageContext } from '../../../context/LanguageContext'
import { util } from '../../../util'

const SelectInput = ({
    capitalize,
    className,
    color,
    defaultValue,
    disabled,
    error,
    inputProps,
    inputRef,
    inputType,
    isFullWidth,
    isLoading = false,
    isModified = false,
    isOptional,
    isOptionsDisabled = false,
    label,
    margin,
    name,
    onBlur,
    onChange,
    onClick,
    onTextChange,
    options,
    placeholder,
    readOnly = true,
    required,
    size,
    value,
    viewOnly = false,
}) => {
    //Localization
    const { rtl, strings } = useContext(LanguageContext)

    //State and Refs
    const [showOptions, setShowOptions] = useState(false)
    const container = useRef(null)
    const optionsRef = useRef(null)
    const selectRef = useRef(null)

    useEffect(() => {
        document.addEventListener('mousedown', outerClickHandler, true)
        return () => document.removeEventListener('mousedown', outerClickHandler, true)
    }, [showOptions])

    useLayoutEffect(() => {
        if (selectRef.current && optionsRef.current && isFullWidth) {
            optionsRef.current.style.width = `${selectRef.current.offsetWidth}px`
        }
    }, [selectRef, optionsRef, showOptions, isFullWidth])

    const outerClickHandler = e => {
        if (container.current && !container.current.contains(e.target)) {
            setShowOptions(false)
        }
    }

    const showOptionHandler = () =>
        !isOptionsDisabled && !disabled && !viewOnly && setShowOptions(!showOptions)

    const optionHandler = value => {
        setShowOptions(false)
        !!onChange && onChange(value, name)
    }

    const onTextEdit = e => {
        setShowOptions(false)
        !!onTextChange && onTextChange(e)
    }

    const renderInputProps = placement => {
        if (viewOnly) {
            return null
        }

        if (!inputProps || !inputProps[placement]) {
            return (
                <Icon
                    className={'select-container__drop-icon'}
                    size={'md'}
                    renderIcon={showOptions ? util.ArrowDropUpIcon : util.ArrowDropDownIcon}
                />
            )
        }

        return (
            <div className={'select-container__icons'}>
                {React.Children.toArray(inputProps[placement]()).map(child =>
                    React.cloneElement(child, {
                        onClick: () =>
                            !child.props.onClick ? showOptionHandler() : child.props.onClick(),
                    })
                )}
            </div>
        )
    }

    const renderSelectOptions = () =>
        showOptions && (
            <ul
                data-testid={'selectInputOptions'}
                className={`select-input__dropdown ${size}`}
                ref={optionsRef}
            >
                {isLoading && (
                    <div className={'dropdown-loader'}>
                        <Spinner loading={isLoading} isInline={true} />
                    </div>
                )}
                {options &&
                    options.map(value => (
                        <li
                            className={'option'}
                            key={value}
                            onClick={() => optionHandler(value)}
                            value={value}
                        >
                            {value}
                        </li>
                    ))}
            </ul>
        )

    const selectInputHandler = e => {
        readOnly ? showOptionHandler() : e.stopPropagation()
    }

    const renderSelectInputValue = () => {
        let inputValue = value || defaultValue || ''
        if (viewOnly && !inputValue) {
            inputValue = '-'
        }

        const inputClassName = util.joinClasses(
            'select-textfield__control',
            readOnly && 'read-only',
            (showOptions || inputValue) && 'has-value',
            capitalize && 'capitalize'
        )

        return (
            <div className={'select-textfield'}>
                <input
                    ref={inputRef || undefined}
                    value={inputValue}
                    required={required}
                    type={inputType}
                    placeholder={placeholder}
                    name={name}
                    autoComplete={'off'}
                    className={inputClassName}
                    readOnly={readOnly || viewOnly}
                    onClick={selectInputHandler}
                    onChange={e => onTextEdit(e)}
                    onBlur={onBlur}
                />
                <span
                    className={util.joinClasses(
                        'input-label',
                        showOptions && 'show-options',
                        rtl && 'rtl'
                    )}
                >
                    {label}
                </span>
            </div>
        )
    }

    const selectContainerClass = util.joinClasses(
        'select-container',
        showOptions && 'show-options',
        disabled && 'select-disabled',
        !!error && 'show-error',
        viewOnly && 'view-only',
        'outline',
        !readOnly && 'edit',
        color,
        isModified && 'modified'
    )

    const selectClickHandler = e => {
        if (isOptionsDisabled) {
            return null
        }
        showOptionHandler()
        !!onClick && onClick(e)
    }

    return (
        <div
            className={util.joinClasses(
                'select-input',
                isOptionsDisabled && 'is-disabled',
                isFullWidth && 'is-full-width',
                margin,
                size,
                className
            )}
            ref={container}
        >
            <div className={selectContainerClass} onClick={selectClickHandler} ref={selectRef}>
                {inputProps && inputProps['start'] && renderInputProps('start')}
                {renderSelectInputValue()}
                {renderInputProps('end')}
            </div>

            {renderSelectOptions()}
            {error && error.length > 0 && (
                <div className={util.joinClasses('select-input__error', size)}>{error}</div>
            )}
            {isOptional && !error && !viewOnly && (
                <Typography
                    size={'sm'}
                    color={'light'}
                    className={`select-input__optional mt-2 mb-3 ${
                        rtl ? 'mr-3 ml-1' : 'ml-3 mr-1'
                    }`}
                    text={strings.OPTIONAL}
                    isItalic
                />
            )}
        </div>
    )
}

SelectInput.propTypes = {
    isOptionsDisabled: PropTypes.bool,

    /** @param className {String} -> className which can override the default design behavior */
    className: PropTypes.string,

    /** @param color {String} -> */
    color: PropTypes.string,

    /** @param defaultValue {*} -> default value that needs to be selected from options */
    defaultValue: PropTypes.any,

    /** @param error {String} -> Error that renders that bottom of the select input component */
    error: PropTypes.string,

    /** @param inputType {String} -> type of the input */
    inputType: PropTypes.string,

    /** @param label {String} -> label that represents the select input */
    label: PropTypes.string,

    /** @param options {Array} -> options */
    options: PropTypes.array,

    /** @param margin {String} -> margin of the selected input component */
    margin: PropTypes.string,

    /** @param name {String} -> name attr for the selected input, it helps to uniquely identify the select input */
    name: PropTypes.string,

    /** @param onChange {EventListener} -> on change event handler that receives value and name of select input component (name comes in handy when you are trying to update the state) */
    onChange: PropTypes.func,

    /** @param placeholder {String} -> placeholder for the select input */
    placeholder: PropTypes.string,

    /** @param readOnly {Boolean} -> if true then you can't edit input or */
    readOnly: PropTypes.bool,

    /** @param inputProps {Object} -> inputProps contains either start or end or both, each key value is a callback which return an JSX (Icons) */
    inputProps: PropTypes.shape({
        start: PropTypes.func,
        end: PropTypes.func,
    }),

    /** @param required {Boolean} -> */
    required: PropTypes.bool,

    /** @param size {String} -> size of the select input {xxs, xs, sm, md, lg, xl, xxl} please refer selectInput.scss */
    size: PropTypes.oneOf(['xxs', 'xs', 'sm', 'rg', 'md', 'lg', 'xl', 'xxl']),

    /** @param value {*} -> value of the selected option from the list of options */
    value: PropTypes.any,

    /** @param onBlur {EventListener} -> */
    onBlur: PropTypes.func,
}

export default memo(SelectInput)
